import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button, notification, Modal, Form, Input, List, Badge, Space, Typography, Tag } from 'antd';
import { LogoutOutlined, SettingOutlined, MailOutlined, SendOutlined } from '@ant-design/icons';
import CheckoutInterface from '../../components/checkout/CheckoutInterface';
import { getCashierSession, logoutCashier, loginCashier, updateCashierPassword, persistCashierSession } from '../../utils/cashierSession';
import { database, ref, push, set, update, get, onValue } from '../Auth/db/firebase';

const CashierLocal = () => {
  const { facilityId } = useParams();
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [passwordForm] = Form.useForm();
  const [inboxOpen, setInboxOpen] = useState(false);
  const [messageForm] = Form.useForm();
  const [sendingMessage, setSendingMessage] = useState(false);
  const [updatingPassword, setUpdatingPassword] = useState(false);
  const [messages, setMessages] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [api, contextHolder] = notification.useNotification();
  const { Text } = Typography;
  const { TextArea } = Input;

  // Set page title
  useEffect(() => {
    document.title = `${facilityId} - Cashier Mode | KioStock`;
  }, [facilityId]);

  useEffect(() => {
    const s = getCashierSession(facilityId);
    if (!s) {
      api.info({ message: 'No Cashier Session', description: 'Login from the facility page to access cashier mode.' });
      navigate(`/${facilityId}/`);
    } else {
      setSession(s);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facilityId, navigate]);

  useEffect(() => {
    if (!session) return;
    if (session.email && session.name) return;

    let cancelled = false;
    const enrichSession = async () => {
      try {
        const cashierRef = ref(database, `cashiers/${facilityId}/${session.id}`);
        const snapshot = await get(cashierRef);
        if (!snapshot.exists()) return;
        const data = snapshot.val();
        const enriched = {
          ...session,
          name: data.name || session.name,
          email: data.email || session.email
        };
        if (cancelled) return;
        setSession(enriched);
        persistCashierSession(facilityId, { id: enriched.id, name: enriched.name, email: enriched.email });
      } catch (err) {
        console.error('Failed to enrich cashier session', err);
      }
    };

    enrichSession();

    return () => {
      cancelled = true;
    };
  }, [session, facilityId]);

  useEffect(() => {
    if (!facilityId || !session) return;

    const messagesRef = ref(database, `messages/${facilityId}`);
    const unsubscribe = onValue(messagesRef, (snapshot) => {
      if (!snapshot.exists()) {
        setMessages([]);
        setUnreadCount(0);
        return;
      }

      const allMessages = Object.entries(snapshot.val()).map(([id, msg]) => ({ id, ...msg }));
      const emailLower = (session.email || '').toLowerCase();

      const relevant = allMessages.filter((msg) => {
        if (!msg) return false;
        if (msg.cashierId && msg.cashierId !== session.id) return false;

        if (msg.from === 'cashier') {
          if (msg.cashierId) {
            return msg.cashierId === session.id;
          }
          const senderEmail = (msg.cashierEmail || '').toLowerCase();
          return senderEmail && emailLower && senderEmail === emailLower;
        }

        if (msg.to === 'cashier') {
          if (msg.cashierId) {
            return msg.cashierId === session.id;
          }
          const targetEmail = (msg.cashierEmail || msg.recipientEmail || '').toLowerCase();
          if (targetEmail && emailLower) {
            return targetEmail === emailLower;
          }
          return true;
        }

        return false;
      });

      relevant.sort((a, b) => {
        const timeA = new Date(a.timestamp || 0).getTime();
        const timeB = new Date(b.timestamp || 0).getTime();
        return timeA - timeB;
      });

      setMessages(relevant);
      const unread = relevant.filter((msg) => msg.to === 'cashier' && (msg.status === 'unread' || !msg.status));
      setUnreadCount(unread.length);
    });

    return () => unsubscribe();
  }, [facilityId, session]);

  const handleCheckout = async (transaction) => {
    // Save under daily bucket with cashier attribution and update inventory
    try {
      const cashierInfo = session?.name || 'Unknown';
      const today = new Date().toISOString().split('T')[0];
      const dayRef = ref(database, `transactions/${facilityId}/${today}`);
      const newRef = push(dayRef);

      await set(newRef, {
        ...transaction,
        id: newRef.key,
        date: today,
        cashier: cashierInfo,
      });

      // Update inventory quantities (robust numeric handling)
      for (const item of transaction.items || []) {
        const itemRef = ref(database, `inventory/${facilityId}/items/${item.name}`);
        const snap = await get(itemRef);
        if (snap.exists()) {
          const current = snap.val();
          const currQty = Number(current.quantity) || 0;
          const sold = Number(item.quantity) || 0;
          const newQty = Math.max(0, currQty - sold);
          await update(itemRef, { quantity: newQty, updatedAt: new Date().toISOString() });
        }
      }

      // Notification here is optional since CheckoutInterface already shows success
    } catch (err) {
      // Let upstream show error
      throw err;
    }
  };

  const handlePasswordSave = async () => {
    try {
      if (!session?.email) {
        api.error({ message: 'Missing email', description: 'Your profile is missing an email. Contact an admin.' });
        return;
      }

      const { currentPassword, newPassword, confirmPassword } = await passwordForm.validateFields();
      if (newPassword !== confirmPassword) {
        api.warning({ message: 'Mismatch', description: 'New passwords must match.' });
        return;
      }

      setUpdatingPassword(true);
      await loginCashier(facilityId, session.email, currentPassword);
      await updateCashierPassword(facilityId, session.id, newPassword);
      const refreshedSession = await loginCashier(facilityId, session.email, newPassword);
      setSession(refreshedSession);
      passwordForm.resetFields();
      setSettingsOpen(false);
      api.success({ message: 'Password updated', description: 'Use your new password next time you log in.' });
    } catch (error) {
      if (error?.errorFields) {
        return;
      }
      api.error({ message: 'Update failed', description: error?.message || 'Unable to change password.' });
    } finally {
      setUpdatingPassword(false);
    }
  };

  const markMessageAsRead = useCallback(async (msg) => {
    if (!msg || msg.to !== 'cashier' || msg.status === 'read') return;
    try {
      const msgRef = ref(database, `messages/${facilityId}/${msg.id}`);
      const snapshot = await get(msgRef);
      if (!snapshot.exists()) return;
      const payload = snapshot.val();
      await set(msgRef, {
        ...payload,
        status: 'read'
      });
    } catch (err) {
      console.error('Failed to mark message as read', err);
    }
  }, [facilityId]);

  const handleSendMessage = async () => {
    try {
      const values = await messageForm.validateFields();
      const message = values.message?.trim();
      if (!message) {
        api.warning({ message: 'Empty message', description: 'Write a short message before sending.' });
        return;
      }

      setSendingMessage(true);
      const messagesRef = ref(database, `messages/${facilityId}`);
      const newMessageRef = push(messagesRef);
      await set(newMessageRef, {
        from: 'cashier',
        to: 'admin',
        message,
        timestamp: new Date().toISOString(),
        status: 'unread',
        facilityId,
        cashierId: session?.id,
        cashierName: session?.name,
        cashierEmail: session?.email || '',
        senderName: session?.name,
        senderEmail: session?.email || ''
      });
      messageForm.resetFields();
      api.success({ message: 'Message sent', description: 'Your admin has been notified.' });
    } catch (error) {
      if (error?.errorFields) {
        return;
      }
      api.error({ message: 'Send failed', description: error?.message || 'Unable to send message right now.' });
    } finally {
      setSendingMessage(false);
    }
  };

  useEffect(() => {
    if (!inboxOpen) return;
    messages.forEach((msg) => {
      if (msg.to === 'cashier' && (msg.status === 'unread' || !msg.status)) {
        markMessageAsRead(msg);
      }
    });
  }, [inboxOpen, messages, markMessageAsRead]);

  if (!session) {
    return (
      <div className="cashier-container">
        {contextHolder}
      </div>
    );
  }

  return (
    <div className="cashier-container">
      {contextHolder}
      
      <div className="cashier-page-header">
        <div className="cashier-greeting">
          Welcome, {session.name}
          {session.email && (
            <Text style={{ display: 'block', fontSize: 13, color: 'var(--text-secondary)' }}>
              {session.email}
            </Text>
          )}
        </div>
        <Space size={12} wrap>
          <Badge count={unreadCount} size="small">
            <Button
              icon={<MailOutlined />} 
              onClick={() => setInboxOpen(true)}
              size="large"
            >
              Inbox
            </Button>
          </Badge>
          <Button
            icon={<SettingOutlined />}
            onClick={() => setSettingsOpen(true)}
            size="large"
          >
            Settings
          </Button>
          <Button 
            icon={<LogoutOutlined />} 
            onClick={() => { logoutCashier(facilityId); navigate(`/${facilityId}/`); }}
            size="large"
          >
            Logout
          </Button>
        </Space>
      </div>
      
      <CheckoutInterface facilityId={facilityId} showTitle={false} onCheckout={handleCheckout} />

      <Modal
        title="Account settings"
        open={settingsOpen}
        onCancel={() => {
          setSettingsOpen(false);
          passwordForm.resetFields();
        }}
        onOk={handlePasswordSave}
        okText="Update password"
        confirmLoading={updatingPassword}
        width={420}
      >
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <div>
            <Text strong style={{ display: 'block' }}>Signed in as</Text>
            <Text>{session.email || 'Unknown email'}</Text>
          </div>
          <Form form={passwordForm} layout="vertical">
            <Form.Item
              label="Current password"
              name="currentPassword"
              rules={[{ required: true, message: 'Enter your current password' }]}
            >
              <Input.Password autoComplete="current-password" placeholder="Current password" />
            </Form.Item>
            <Form.Item
              label="New password"
              name="newPassword"
              rules={[
                { required: true, message: 'Enter a new password' },
                { min: 6, message: 'Use at least 6 characters' }
              ]}
            >
              <Input.Password autoComplete="new-password" placeholder="New password" />
            </Form.Item>
            <Form.Item
              label="Confirm new password"
              name="confirmPassword"
              dependencies={["newPassword"]}
              rules={[
                { required: true, message: 'Confirm your new password' },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('newPassword') === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error('Passwords do not match'));
                  }
                })
              ]}
            >
              <Input.Password autoComplete="new-password" placeholder="Confirm new password" />
            </Form.Item>
          </Form>
        </Space>
      </Modal>

      <Modal
        title="Inbox"
        open={inboxOpen}
        onCancel={() => {
          setInboxOpen(false);
          messageForm.resetFields();
        }}
        footer={[
          <Button key="cancel" onClick={() => {
            setInboxOpen(false);
            messageForm.resetFields();
          }}>
            Close
          </Button>,
          <Button
            key="send"
            type="primary"
            icon={<SendOutlined />}
            loading={sendingMessage}
            onClick={handleSendMessage}
          >
            Send message
          </Button>
        ]}
        width={720}
      >
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <Form form={messageForm} layout="vertical">
            <Form.Item
              label="Message to admin"
              name="message"
              rules={[{ required: true, message: 'Enter your message' }]}
            >
              <TextArea rows={4} maxLength={500} showCount placeholder="Describe what you need help with" />
            </Form.Item>
          </Form>

          {messages.length === 0 ? (
            <Text type="secondary">No messages yet. Start a conversation with your admin.</Text>
          ) : (
            <List
              dataSource={messages}
              renderItem={(msg) => {
                const isUnread = msg.to === 'cashier' && (msg.status === 'unread' || !msg.status);
                const senderLabel = msg.from === 'cashier' ? 'You' : 'Admin';
                const senderColor = msg.from === 'cashier' ? 'blue' : 'green';
                return (
                  <List.Item
                    key={msg.id}
                    onClick={() => markMessageAsRead(msg)}
                    style={{
                      cursor: 'pointer',
                      background: isUnread ? 'var(--bg-tertiary)' : 'transparent',
                      borderRadius: 8,
                      padding: 16,
                      border: '1px solid var(--border-tertiary)',
                      marginBottom: 12
                    }}
                  >
                    <Space direction="vertical" style={{ width: '100%' }} size={8}>
                      <Space align="center" size={10} wrap>
                        <Tag color={senderColor}>{senderLabel}</Tag>
                        {msg.cashierName && msg.from === 'admin' && (
                          <Tag>{msg.cashierName}</Tag>
                        )}
                        {isUnread && <Badge status="processing" text="New" />}
                      </Space>
                      <Text style={{ color: 'var(--text-primary)' }}>{msg.message}</Text>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {msg.timestamp ? new Date(msg.timestamp).toLocaleString() : ''}
                      </Text>
                    </Space>
                  </List.Item>
                );
              }}
            />
          )}
        </Space>
      </Modal>
    </div>
  );
};

export default CashierLocal;
